// Стек навигации телефонной оболочки.
//
// Живёт отдельно от `appStore`: десктопные раскладки, ширины панелей и
// открытые окна на телефоне не нужны, а мешались бы. Общие данные (сессии,
// дерево, лента) по-прежнему в `appStore` — здесь только «где я сейчас».
//
// Системная кнопка «назад» на Android обязана возвращать по стеку, а не
// закрывать приложение, поэтому каждый уход вглубь делает `history.pushState`,
// а `popstate` снимает верхний экран (M1.4).

import { create } from 'zustand'
import { load, saveIfChanged } from '../lib/persist'
import type { MobileScreen, MobileTab } from './nav'

interface SavedNav { tab: MobileTab }

const saved = load<SavedNav>('mobile', { tab: 'base' })
const asTab = (value: unknown): MobileTab =>
  value === 'feed' || value === 'chats' || value === 'search' || value === 'messengers'
    ? value
    : 'base'

interface MobileState {
  tab: MobileTab
  /** Экраны поверх корневого: последний — видимый. */
  stack: MobileScreen[]
  screen: MobileScreen
  setTab: (tab: MobileTab) => void
  push: (screen: MobileScreen) => void
  /** Вернуться на экран назад. False — возвращать уже некуда.
   *
   *  Звать напрямую не надо: снимает экран только обработчик popstate из
   *  `watchHistoryBack`, а кнопка «‹» в шапке делает `history.back()`. Так
   *  стек и history остаются в ногу — иначе системная кнопка «назад» начнёт
   *  проматывать по два экрана за нажатие. */
  pop: () => boolean
}

/** Сколько ближайших popstate вызвано нашей же перемоткой history и должно
 *  быть пропущено обработчиком. */
let ours = 0

export const useMobileStore = create<MobileState>((set, get) => ({
  tab: asTab(saved.tab),
  stack: [],
  screen: { kind: 'tab', tab: asTab(saved.tab) },

  setTab: (tab) => {
    // Смена вкладки сбрасывает стек: возвращаться из «Заметки» во вкладку
    // «Беседы» бессмысленно, а копить чужие экраны — путать человека.
    const { stack } = get()
    if (stack.length > 0) {
      // Перемотка вызовет popstate, но экран уже снят здесь — обработчик
      // должен этот один раз промолчать, иначе он решит, что стек пуст, и
      // добавит в history лишнюю запись. С каждой такой сменой вкладки
      // history росла бы на запись и никогда не сжималась.
      ours += 1
      history.go(-stack.length)
    }
    set({ tab, stack: [], screen: { kind: 'tab', tab } })
  },

  push: (screen) => {
    history.pushState({ ab: true }, '')
    set((s) => ({ stack: [...s.stack, screen], screen }))
  },

  pop: () => {
    const { stack, tab } = get()
    if (stack.length === 0) return false
    const rest = stack.slice(0, -1)
    set({
      stack: rest,
      screen: rest[rest.length - 1] ?? { kind: 'tab', tab },
    })
    return true
  },
}))

useMobileStore.subscribe((s) => saveIfChanged('mobile', { tab: s.tab }))

/** Одна на приложение подписка на системную кнопку «назад».
 *
 *  Идемпотентна намеренно: StrictMode в dev монтирует эффекты дважды, а сама
 *  оболочка пересоздаётся при смене раскладки телефон ↔ десктоп. Вторая
 *  подписка положила бы в history лишнюю запись и снимала бы по два экрана
 *  за нажатие, поэтому отписки здесь нет — подписка живёт со страницей.
 */
let watching = false

export function watchHistoryBack(): void {
  if (watching) return
  watching = true
  const onPop = () => {
    // Перемотка от смены вкладки: экран уже снят, второй раз снимать нечего.
    if (ours > 0) {
      ours -= 1
      return
    }
    // Стек пуст — уходить некуда: возвращаем запись обратно, иначе следующее
    // нажатие закроет приложение.
    if (!useMobileStore.getState().pop()) history.pushState({ ab: true }, '')
  }
  history.pushState({ ab: true }, '')
  window.addEventListener('popstate', onPop)
}
